import { core } from '@musinsa/pbo-core'
import { DETAIL_DEFAULT_PAGE_SIZE, DETAIL_MAX_PAGE_SIZE, DETAIL_MID_PAGE_SIZE, SPLIT_TOP_PERCENT } from './constants'
import { TableGroup } from './TableGroup'

/** 상세 요청을 만드는 헤더 행 식별 필드. RP12 는 값이 null 일 수 있어 타입을 넓게 받는다. */
interface HeadIdentity {
  shipperCode: unknown
  closeDate: unknown
  warehouseName: unknown
}

/** 상세를 연 상단 행의 식별 조건 — 상세 요청의 화주·마감일자·주문센터. */
export type SelectedHead<THead extends HeadIdentity> = Pick<THead, 'shipperCode' | 'closeDate' | 'warehouseName'>

export interface DailyCloseTablesProps<THead extends HeadIdentity, TItem extends object> {
  heads: THead[]
  items: TItem[]
  itemPageInfo: PageInfoType
  selectedHead?: SelectedHead<THead> | null
  isHeadLoading?: boolean
  isItemLoading?: boolean
  isDownloading?: boolean
  onHeadDoubleClick: (row: THead) => void
  onItemPage: (page: number, pageSize: number) => void
  onDownload: (target: 'head' | 'item') => void
}

interface Props<THead extends HeadIdentity, TItem extends object> extends DailyCloseTablesProps<THead, TItem> {
  headColumns: core.CoreColumnType<THead>[]
  itemColumns: core.CoreColumnType<TItem>[]
}

const ITEM_PAGE_SIZE_OPTIONS = [String(DETAIL_DEFAULT_PAGE_SIZE), String(DETAIL_MID_PAGE_SIZE), String(DETAIL_MAX_PAGE_SIZE)]

/** RP12·RP13 상단 헤더·하단 상세 표. 1.0 처럼 상단 행을 더블클릭하면 그 행 조건으로 상세를 조회한다. */
export function DailyCloseSplitTables<THead extends HeadIdentity, TItem extends object>({
  heads,
  items,
  itemPageInfo,
  selectedHead,
  isHeadLoading,
  isItemLoading,
  isDownloading,
  onHeadDoubleClick,
  onItemPage,
  onDownload,
  headColumns,
  itemColumns,
}: Readonly<Props<THead, TItem>>) {
  const isPicked = (row: THead) =>
    selectedHead != null &&
    row.shipperCode === selectedHead.shipperCode &&
    row.closeDate === selectedHead.closeDate &&
    row.warehouseName === selectedHead.warehouseName

  return (
    <core.SplitPane topPercent={SPLIT_TOP_PERCENT} topCount={1}>
      <TableGroup<THead>
        columns={headColumns}
        rows={heads}
        total={heads.length}
        loading={isHeadLoading}
        downloadDisabled={!heads.length || isDownloading}
        onDownload={() => onDownload('head')}
        onRowDoubleClick={onHeadDoubleClick}
        isPicked={isPicked}
      />
      <TableGroup<TItem>
        columns={itemColumns}
        rows={items}
        total={itemPageInfo.totalCount}
        loading={isItemLoading}
        pager={{
          page: itemPageInfo.page || 1,
          pageSize: itemPageInfo.size || DETAIL_DEFAULT_PAGE_SIZE,
          pageSizeOptions: ITEM_PAGE_SIZE_OPTIONS,
          onPage: onItemPage,
        }}
        downloadDisabled={!items.length || isDownloading}
        onDownload={() => onDownload('item')}
        emptyText={selectedHead ? undefined : '상단 목록에서 행을 더블클릭하면 표시됩니다'}
      />
    </core.SplitPane>
  )
}
